<template>
  <div class="station">
    <div ref="container" @mousedown="onMousedown"></div>
    <div class="station__controls">
      <div
        class="station__controls-button station__controls-button--share"
        @click="share"
      ></div>
      <div
        v-if="playing"
        class="station__controls-button station__controls-button--pause"
        @click="pause"
      ></div>
      <div
        v-else
        class="station__controls-button station__controls-button--play"
        @click="play"
      ></div>
      <div
        v-if="fullScreenEnabled"
        class="station__controls-button station__controls-button--exit-fullscreen"
        @click="fullscreen"
      ></div>
      <div
        v-else
        class="station__controls-button station__controls-button--enter-fullscreen"
        @click="fullscreen"
      ></div>
    </div>
    <template v-if="!loading">
      <key-point
        :position="keyPoint.position"
        :lookAt="webGL.camera.position"
        :picto="keyPoint.picto"
        :rotation="{x: 0, y: Math.PI * .25, z: 0}"
        :key="index"
        v-for="(keyPoint, index) in keyPoints"
        :title="keyPoint.title"
        :content="keyPoint.content"
        :isSelected="keyPoint.isSelected"
        @click="selectKeyPoint(index)"
      />
    </template>
    <transition name="t-translate__right">
      <info
        class="station__info"
        v-if="selectedKeyPoint"
        :title="selectedKeyPoint.title"
        :content="selectedKeyPoint.content"
        :picto="selectedKeyPoint.picto"
        @previous="selectPreviousKeyPoint"
        @next="selectNextKeyPoint"
        @close="pause"
      />
    </transition>
    <transition name="t-fader">
      <share
        v-if="shareOpened"
        @close="shareOpened = false"
      />
    </transition>
    <transition name="t-fader">
      <loader v-if="loading" />
    </transition>
    <a class="station__link" href="https://brig.ht" target="_blank">
      <img class="station__logo" src="/images/logo.png">
    </a>
  </div>
</template>

<script>
import { gsap } from 'gsap'
import data from '@/assets/data/data.json'

import { EnvironmentStation } from '@/webGL/objects/EnvironmentStation'
import { WebGL } from '@/webGL/WebGL'

import CameraControls from 'camera-controls'
// import { gui } from '@/utils/gui'

import KeyPoint from '@/components/KeyPoint'
import Info from '@/components/Info'
import Share from '@/components/Share'
import Loader from '@/components/Loader'

import {
  Vector3,
  Object3D,
  DirectionalLight,
  AmbientLight,
  AxesHelper,
  Quaternion
} from 'three'

import * as THREE from 'three'

CameraControls.install({ THREE })

const vector3 = new Vector3()
const quaternionA = new Quaternion()
const quaternionB = new Quaternion()

export default {

  data () {
    data.forEach(keyPoint => { keyPoint.isSelected = false })

    return {
      selectedIndex: -1,
      keyPoints: data,
      playing: false,
      fullScreenEnabled: false,
      shareOpened: false,
      loading: true
    }
  },

  components: {
    KeyPoint,
    Info,
    Share,
    Loader
  },

  created () {
    this.onMousemove = this.onMousemove.bind(this)
    this.onMouseup = this.onMouseup.bind(this)

    this.promises = []

    this.webGL = new WebGL()

    // CAMERA SETUP
    this.webGL.camera.fov = 15
    this.cameraTarget = new Vector3(-4, 6, 0)
    this.cameraPosition = new Vector3(80, 55, 90)
    this.cameraTransition = null
    this.webGL.camera.position.copy(this.cameraPosition)
    this.webGL.camera.lookAt(this.cameraTarget)

    this.webGL.renderer.webGLRenderer.setClearColor(0xe3e3e3)

    // LIGHTS SETUP
    this.ambient = new AmbientLight(0xffffff, .9)
    // gui.add(this.ambient, 'intensity').min(0).max(1.5).step(0.1).name('light2')

    this.directional = new DirectionalLight(0xffffff, .4)
    this.directional.position.set(30, 30, 40)
    this.directional.castShadow = true
    this.directional.lookAt( new Vector3(0, 0, 0) )
    this.directional.shadow.camera.bottom = -40
    this.directional.shadow.camera.top = 40
    this.directional.shadow.camera.left = -40
    this.directional.shadow.camera.right = 40
    this.directional.shadow.mapSize.width = 1024
    this.directional.shadow.mapSize.height = 1024
    this.directional.shadow.bias = -0.0005
    this.directional.shadow.camera.updateProjectionMatrix()

    // gui.add(this.directional, 'intensity').min(0).max(1).step(0.1).name('directionnal')
    // gui.add(this.directional.position, 'x').min(-30).max(30).step(1).name('position')

    // OBJECTS SETUP
    this.environment = new EnvironmentStation()
    this.promises.push( this.environment.promise )

    // ADD ALL STUFFS
    this.webGL.scene.add(this.environment, this.directional, this.ambient)

    Promise.all(this.promises).then(()=>{
      this.$emit('ready')
      this.loading = false
    })
  },

  mounted () {
    this.webGL.resize()
    this.webGL.el = this.$refs.container
    this.$dom3d.setCamera(this.webGL.camera)

    this.controls = new CameraControls(this.webGL.camera, this.$refs.container)
    this.controls.dollySpeed = 0.1
    this.controls.minPolarAngle = Math.PI / 10
    this.controls.maxPolarAngle = Math.PI / 2
    this.controls.minDistance = 40
    this.controls.maxDistance = 200
    this.controls.minAzimuthAngle = -Math.PI / 3
    this.controls.maxAzimuthAngle = 9 * Math.PI / 20
    this.controls.mouseButtons.right = CameraControls.ACTION.NONE
    this.controls.draggingDampingFactor = 0.1
    this.controls.setLookAt(80, 55, 90, -4, 6, 0)
    this.controls.saveState()
  },

  computed: {
    selectedKeyPoint () {
      return this.selectedIndex >= 0 ? this.keyPoints[this.selectedIndex] : null
    }
  },

  methods: {
    onUpdate (delta) {
      this.controls.update(delta / 1000)
    },

    selectKeyPoint (index) {
      const keyPoint = this.keyPoints[index]
      const { eye, position } = keyPoint
      this.clearSelectedKeyPoint()
      this.$set(keyPoint, 'isSelected', true)
      this.selectedIndex = index
      this.controls.setLookAt(
        eye.x,
        eye.y,
        eye.z,
        position.x,
        position.y,
        position.z,
        true
      )
    },

    clearSelectedKeyPoint () {
      for (let i = 0; i < this.keyPoints.length; i++) {
        this.$set(this.keyPoints[i], 'isSelected', false)
      }

      this.selectedIndex = -1
      this.controls.reset(true)
    },

    selectNextKeyPoint () {
      this.selectKeyPoint((this.selectedIndex + 1) % this.keyPoints.length)
      if (this.playing) {
        clearTimeout(this.playTimeout)
        this.playTimeout = setTimeout(() => this.selectNextKeyPoint(), 8000)
      }
    },

    selectPreviousKeyPoint () {
      this.selectKeyPoint(this.selectedIndex <= 0
        ? this.keyPoints.length - 1
        : this.selectedIndex - 1
      )

      if (this.playing) {
        clearTimeout(this.playTimeout)
        this.playTimeout = setTimeout(() => this.selectNextKeyPoint(), 8000)
      }
    },

    play () {
      if (this.playing) return
      this.playing = true

      if (this.selectedIndex === -1) {
        this.selectNextKeyPoint()
      } else {
        this.playTimeout = setTimeout(() => this.selectNextKeyPoint(), 4000)
      }
    },

    pause () {
      if (this.playing) {
        this.playing = false
        clearTimeout(this.playTimeout)
      }

      this.clearSelectedKeyPoint()
    },

    fullscreen () {
      this.fullScreenEnabled = !document.fullscreenElement
      this.fullScreenEnabled
        ? document.body.requestFullscreen()
        : document.exitFullscreen()
    },

    onResize () {
      this.fullScreenEnabled = !!document.fullscreenElement
    },

    onMousemove () {
      this.$el.removeEventListener('mouseup', this.onMouseup)
    },

    onMouseup () {
      removeEventListener('mousemove', this.onMousemove)
      this.clearSelectedKeyPoint()
    },

    onMousedown () {
      addEventListener('mousemove', this.onMousemove, { once: true })
      this.$el.addEventListener('mouseup', this.onMouseup, { once: true })
    },

    share () {
      this.shareOpened = true
    }
  }
}
</script>

<style lang="stylus">
@import '~@/styles/settings/variables'

.station
  &__info
    top 0
    right 0
    width 450px
    max-width 40%
    position fixed
    z-index 10
    box-shadow 0 0 20px rgba($colors.black, .1)

  &__controls
    bottom 30px
    right 30px
    z-index: 20
    position absolute
    display flex

  &__link
    width 45px
    z-index 10
    left 30px
    bottom 30px
    position absolute
    display block

  &__logo
    width 100%
    height auto
    display block

  &__controls-button
    background-size: contain
    background-position: center center
    width 24px
    height 24px
    margin-left: 30px
    flex-grow 0
    flex-shrink 0
    display block
    cursor pointer
    opacity 0.5
    transition .2s

    &:hover
      opacity 1
      transform scale(1.05)

    &--share
      background-image url(/images/share.svg)

    &--play
      background-image url(/images/play.svg)

    &--pause
      background-image url(/images/pause.svg)

    &--enter-fullscreen
      background-image url(/images/enter-fullscreen.svg)

    &--exit-fullscreen
      background-image url(/images/exit-fullscreen.svg)
</style>
